import {
  keepPreviousData,
  QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysAnalytics } from '../queryKeys';

import { fetchAnalyticsKpi } from './fetchAnalyticsKpi';
import { AnalyticsKpiQueryParams, AnalyticsKpiResponse } from './types';
import { zodAnalyticsKpiQueryParamsSchema } from './validations';

/**
 * Generates the query options for the analytics KPI query.
 *
 * @param queryParams - The query parameters for fetching the analytics KPI.
 * @param options - Additional options for the query.
 * @param ignoreSignal - If true, the abort signal is not passed to the fetch function.
 *   Use this for prefetching in route loaders to prevent request cancellation when the loader completes.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getAnalyticsKpiQueryOptions = ({
  queryParams,
  options,
  ignoreSignal = false,
}: {
  queryParams: Partial<AnalyticsKpiQueryParams>;
  options?: Omit<
    UseQueryOptions<AnalyticsKpiResponse, Error, AnalyticsKpiResponse>,
    'queryKey' | 'queryFn'
  >;
  ignoreSignal?: boolean;
}) => {
  const validatedQueryParams =
    zodAnalyticsKpiQueryParamsSchema.parse(queryParams);

  return {
    enabled: Boolean(validatedQueryParams.kpiName),
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchAnalyticsKpi(validatedQueryParams, {
        signal: ignoreSignal ? undefined : signal,
      }),
    queryKey: queryKeysAnalytics.getKpi(validatedQueryParams),
    staleTime: ms('5m'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the data for a specific analytics KPI with given query parameters.
 *
 * @param queryParams - The query parameters for fetching the analytics KPI.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/User-Activity/operation/get_user_activity_active_users_user_activity_active_users_post
 */
export const useQueryAnalyticsKpi = (
  queryParams: Partial<AnalyticsKpiQueryParams>,
  options?: Omit<
    UseQueryOptions<AnalyticsKpiResponse, Error, AnalyticsKpiResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getAnalyticsKpiQueryOptions({
      options,
      queryParams,
    }),
    placeholderData: keepPreviousData,
  });
